import { Navigate, Outlet, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import { useAuth } from "../hooks";

interface AuthGuardProps {
  /**
   * Optional custom claim key to check on the user's customClaims.
   * If provided, the claim must exist and be truthy (e.g., "platform_admin").
   */
  requiredClaim?: string;
  /**
   * Where to redirect when user is authenticated but lacks the required claim.
   * Defaults to "/login". Business app sets this to "/register" so new users
   * can sign up instead of being trapped in a login loop.
   */
  claimMissingRedirect?: string;
  /**
   * Optional app-owned presentation for the auth-resolution state. The guard
   * continues to own all redirect and claim behavior; this seam lets a cut-over
   * app render its native design system without changing untouched apps.
   */
  loadingFallback?: ReactNode;
}

export function AuthGuard({
  requiredClaim,
  claimMissingRedirect,
  loadingFallback,
}: AuthGuardProps) {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    if (loadingFallback !== undefined) return loadingFallback;
    return <span role="status">Loading account…</span>;
  }

  if (!user) {
    const returnUrl = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?returnUrl=${returnUrl}`} replace />;
  }

  if (requiredClaim && !user.customClaims?.[requiredClaim]) {
    // User is authenticated but lacks the required claim for this app.
    // Redirect to a custom path (e.g., /register) or /login.
    return <Navigate to={claimMissingRedirect ?? "/login"} replace />;
  }

  return <Outlet />;
}
